
import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import MessageList from "./chat/MessageList";
import { ChatMessage } from "./chat/types";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { 
  getDemoResponse, 
  getSignupPromptMessage, 
  MAX_FREE_MESSAGES,
  demoSuggestions
} from "@/utils/demoResponses";
import featureFlags from "@/lib/featureFlags";

const IntegrationDemo = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "1",
      content: "👋 Hi team, I just joined the channel! I'm your VirtuStaff assistant. Ask me about creating social media posts, email campaigns, or any other tasks you need help with! ✨",
      sender: "ai",
      timestamp: new Date(Date.now() - 3600000),
      mentions: []
    }
  ]);
  
  const [isTyping, setIsTyping] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);

  const handleSendMessage = async (text: string) => {
    if (!text.trim()) return;
    
    // Only add user message with just @VirtuStaff tag once (no duplicate mentions)
    const messageText = text.startsWith("@VirtuStaff") ? text : `@VirtuStaff ${text}`;
    
    // Add user message
    const userMessage = {
      id: Date.now().toString(),
      content: messageText,
      sender: "user" as const,
      timestamp: new Date(),
      mentions: ["@VirtuStaff"]
    };
    
    setMessages([...messages, userMessage]);
    
    // Simulate AI typing
    setIsTyping(true);
    
    // Increment message count
    const newMessageCount = messageCount + 1;
    setMessageCount(newMessageCount);
    
    // Simulate delay - shorter for better UX in demo
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 600));
    
    let response;
    
    // Show signup prompt after MAX_FREE_MESSAGES
    if (newMessageCount >= MAX_FREE_MESSAGES) {
      response = getSignupPromptMessage();
      setShowSignupPrompt(true);
    } else {
      // Get predefined response based on user message
      response = getDemoResponse(messageText);
      
      if (response.isSignupPrompt) {
        setShowSignupPrompt(true);
      }
    }
    
    // Create AI response message
    const aiResponse = {
      id: (Date.now() + 1).toString(),
      content: response.content,
      sender: "ai" as const,
      timestamp: new Date(),
      mentions: []
    };
    
    setMessages(prev => [...prev, aiResponse]);
    setIsTyping(false);
  };
  
  const handleGetStarted = () => {
    if (featureFlags.enableSignup) {
      navigate('/onboarding');
    } else {
      // Scroll to whitelist section
      const whitelistElement = document.getElementById('whitelist-section');
      if (whitelistElement) {
        whitelistElement.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <Card className="border shadow-sm bg-card overflow-hidden">
      <div className="border-b p-3 bg-muted/20">
        <div className="flex items-center">
          <span className="text-sm font-medium">#virtustaff-team</span>
          <Badge variant="outline" className="ml-2 text-xs">Slack Channel</Badge>
        </div>
      </div>
      
      <MessageList 
        messages={messages} 
        isTyping={isTyping} 
      />
      
      {showSignupPrompt ? (
        <div className="p-3 border-t flex flex-col gap-2">
          <p className="text-sm text-muted-foreground">Ready to experience the full power of your AI staff team?</p>
          <Button 
            onClick={handleGetStarted}
            className="bg-primary hover:bg-primary/90 text-white flex items-center gap-1"
          >
            {featureFlags.enableSignup ? 'Sign Up for Free' : 'Join Whitelist'} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="p-3 border-t flex flex-col gap-2">
          <p className="text-xs text-muted-foreground mb-1">
            Select a suggestion to see VirtuStaff in action:
          </p>
          <div className="flex flex-wrap gap-2">
            {demoSuggestions.map((suggestion, index) => (
              <Badge 
                key={index}
                variant="outline" 
                className="cursor-pointer hover:bg-primary/10 transition-colors"
                onClick={() => handleSendMessage(suggestion)}
              >
                {suggestion}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default IntegrationDemo;
